import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Image from '../../components/AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentService } from '../../services/tournamentService';
import { regionalTeamService } from '../../services/regionalTeamService';
import { exportFixturesToPDF, exportFixturesToExcel, exportFixturesToWord } from '../../utils/fixtureExportUtils';
import TournamentKnockoutBracket from './components/TournamentKnockoutBracket';
import ScheduleTournamentGameModal from './components/ScheduleTournamentGameModal';
import { GROUP_STAGE_NAME } from '../../constants/tournamentStages';

const STATUS_OPTIONS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EMPTY_TOURNAMENT_FORM = {
  name: '',
  season: '',
  description: '',
  tournamentStatus: 'upcoming',
  startDate: '',
  endDate: '',
  maxTeams: 16,
};

const EMPTY_FIXTURE_SETTINGS = {
  startDate: '',
  endDate: '',
  timeSlots: ['14:00', '16:00', '18:00'],
  venues: [],
  doubleRoundRobin: false,
  groupId: '',
};

const TournamentGamesOrganizer = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Core data
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [tournamentDetails, setTournamentDetails] = useState(null);
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [regionalTeams, setRegionalTeams] = useState([]);

  // Tournament create/edit modal
  const [isTournamentModalOpen, setIsTournamentModalOpen] = useState(false);
  const [tournamentModalMode, setTournamentModalMode] = useState('create'); // 'create' | 'edit'
  const [tournamentForm, setTournamentForm] = useState(EMPTY_TOURNAMENT_FORM);
  const [isSavingTournament, setIsSavingTournament] = useState(false);

  // Add teams modal
  const [isAddTeamsModalOpen, setIsAddTeamsModalOpen] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [selectedTeamIdsToAdd, setSelectedTeamIdsToAdd] = useState([]);
  const [isAddingTeams, setIsAddingTeams] = useState(false);

  // Manage (remove) teams modal
  const [isManageTeamsModalOpen, setIsManageTeamsModalOpen] = useState(false);
  const [manageTeamsTab, setManageTeamsTab] = useState('current');
  const [teamsToRemove, setTeamsToRemove] = useState([]);
  const [isRemovingTeams, setIsRemovingTeams] = useState(false);

  // Groups
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isGroupGeneratorOpen, setIsGroupGeneratorOpen] = useState(false);
  const [isGeneratingGroups, setIsGeneratingGroups] = useState(false);
  const [groupGeneratorSettings, setGroupGeneratorSettings] = useState({
    groupCount: 4,
    naming: 'letters',
    assignTeams: true,
    distribution: 'balanced',
    replaceExisting: false,
  });

  // Fixture generator modal
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [fixtureSettings, setFixtureSettings] = useState(EMPTY_FIXTURE_SETTINGS);
  const [fixtureStep, setFixtureStep] = useState(1); // 1 Settings, 2 Preview, 3 Confirm
  const [generatedFixtures, setGeneratedFixtures] = useState([]);
  const [isGeneratingFixtures, setIsGeneratingFixtures] = useState(false);

  // Knockout modal
  const [isKnockoutModalOpen, setIsKnockoutModalOpen] = useState(false);

  // Manual schedule game modal
  const [isScheduleGameModalOpen, setIsScheduleGameModalOpen] = useState(false);
  const [scheduleGameInitialGroupId, setScheduleGameInitialGroupId] = useState('');
  const [scheduleGameInitialScope, setScheduleGameInitialScope] = useState('group');

  // Delete tournament modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingTournament, setIsDeletingTournament] = useState(false);

  // Fixtures list sort/reorder state
  const [gameSortBy, setGameSortBy] = useState('date'); // date, time, team, venue
  const [gameSortOrder, setGameSortOrder] = useState('asc');
  const [sortedGames, setSortedGames] = useState([]);
  const [isRearranging, setIsRearranging] = useState(false);

  const userRole = userProfile?.role;
  const hasAccess = userRole === 'admin' || userRole === 'staff';

  useEffect(() => {
    if (authLoading) return;
    if (!hasAccess) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [authLoading, hasAccess, navigate]);

  useEffect(() => {
    if (hasAccess) {
      loadTournaments();
      loadRegionalTeams();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (selectedTournamentId) {
      loadTournamentDetails(selectedTournamentId);
    } else {
      setTournamentDetails(null);
      setGames([]);
    }
  }, [selectedTournamentId]);

  useEffect(() => {
    applySorting(games, gameSortBy, gameSortOrder);
  }, [games, gameSortBy, gameSortOrder]);

  const loadTournaments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tournamentService.getTournaments();
      setTournaments(data || []);
    } catch (err) {
      console.error('Error loading tournaments:', err);
      setError(err.message || 'Failed to load tournaments');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRegionalTeams = async () => {
    try {
      const data = await regionalTeamService.getAll();
      setRegionalTeams(data || []);
    } catch (err) {
      console.error('Error loading regional teams:', err);
    }
  };

  const loadTournamentDetails = async (tournamentId) => {
    setIsLoadingDetails(true);
    try {
      const details = await tournamentService.getTournamentById(tournamentId);
      setTournamentDetails(details);
      await loadGames(tournamentId);
    } catch (err) {
      console.error('Error loading tournament details:', err);
      setError(err.message || 'Failed to load tournament details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const loadGames = async (tournamentId) => {
    setIsLoadingGames(true);
    try {
      const data = await tournamentService.getGamesByTournament(tournamentId);
      setGames(data || []);
    } catch (err) {
      console.error('Error loading tournament games:', err);
    } finally {
      setIsLoadingGames(false);
    }
  };

  const applySorting = (list, sortBy, sortOrder) => {
    const sorted = [...list].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.scheduledDate || 0) - new Date(b.scheduledDate || 0);
          break;
        case 'time': {
          const timeA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
          const timeB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
          comparison = timeA - timeB;
          break;
        }
        case 'team': {
          const aName = `${a.homeTeam?.teamName || ''} vs ${a.awayTeam?.teamName || ''}`;
          const bName = `${b.homeTeam?.teamName || ''} vs ${b.awayTeam?.teamName || ''}`;
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'venue':
          comparison = (a.venue || '').localeCompare(b.venue || '');
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    setSortedGames(sorted);
  };

  const handleSortChange = (sortBy) => {
    const newOrder = gameSortBy === sortBy && gameSortOrder === 'asc' ? 'desc' : 'asc';
    setGameSortBy(sortBy);
    setGameSortOrder(newOrder);
  };

  const displayGames = sortedGames.length > 0 ? sortedGames : games;

  const clearMessagesLater = () => {
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  };

  // ---------------------------------------------------------------------
  // Tournament CRUD
  // ---------------------------------------------------------------------

  const handleOpenCreateTournamentModal = () => {
    setTournamentModalMode('create');
    setTournamentForm(EMPTY_TOURNAMENT_FORM);
    setError(null);
    setIsTournamentModalOpen(true);
  };

  const handleOpenEditTournamentModal = () => {
    if (!tournamentDetails) return;
    setTournamentModalMode('edit');
    setTournamentForm({
      name: tournamentDetails.name || '',
      season: tournamentDetails.season || '',
      description: tournamentDetails.description || '',
      tournamentStatus: tournamentDetails.tournamentStatus || 'upcoming',
      startDate: tournamentDetails.startDate || '',
      endDate: tournamentDetails.endDate || '',
      maxTeams: tournamentDetails.maxTeams || 16,
    });
    setError(null);
    setIsTournamentModalOpen(true);
  };

  const handleTournamentFormChange = (field, value) => {
    setTournamentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveTournament = async (event) => {
    event.preventDefault();
    if (!tournamentForm.name?.trim()) {
      setError('Tournament name is required');
      return;
    }

    setIsSavingTournament(true);
    setError(null);

    try {
      if (tournamentModalMode === 'create') {
        const created = await tournamentService.createTournament(tournamentForm);
        setSuccess('Tournament created successfully!');
        await loadTournaments();
        setSelectedTournamentId(created.id);
      } else {
        await tournamentService.updateTournament(selectedTournamentId, tournamentForm);
        setSuccess('Tournament updated successfully!');
        await loadTournaments();
        await loadTournamentDetails(selectedTournamentId);
      }
      setIsTournamentModalOpen(false);
      clearMessagesLater();
    } catch (err) {
      console.error('Error saving tournament:', err);
      setError(err.message || 'Failed to save tournament');
    } finally {
      setIsSavingTournament(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!selectedTournamentId) return;
    setIsDeletingTournament(true);
    setError(null);

    try {
      await tournamentService.deleteTournament(selectedTournamentId);
      setSuccess('Tournament deleted successfully!');
      setSelectedTournamentId(null);
      setTournamentDetails(null);
      setGames([]);
      await loadTournaments();
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error deleting tournament:', err);
      setError(err.message || 'Failed to delete tournament');
    } finally {
      setIsDeletingTournament(false);
    }
  };

  // ---------------------------------------------------------------------
  // Team management
  // ---------------------------------------------------------------------

  const currentTeamIds = (tournamentDetails?.teams || []).map((tt) => tt.regionalTeamId);
  const availableRegionalTeams = regionalTeams.filter((team) => !currentTeamIds.includes(team.id));
  const filteredAvailableTeams = availableRegionalTeams.filter((team) =>
    (team.teamName || '').toLowerCase().includes(teamSearchTerm.toLowerCase())
  );

  const handleOpenAddTeamsModal = () => {
    if (!selectedTournamentId) return;
    setSelectedTeamIdsToAdd([]);
    setTeamSearchTerm('');
    setError(null);
    setIsAddTeamsModalOpen(true);
  };

  const handleOpenManageTeamsModal = () => {
    if (!selectedTournamentId) return;
    setManageTeamsTab('current');
    setTeamsToRemove([]);
    setSelectedTeamIdsToAdd([]);
    setTeamSearchTerm('');
    setError(null);
    setIsManageTeamsModalOpen(true);
  };

  const handleToggleTeamSelection = (teamId) => {
    setSelectedTeamIdsToAdd((prev) => (prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]));
  };

  const handleToggleTeamRemoval = (teamId) => {
    setTeamsToRemove((prev) => (prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]));
  };

  const handleAddTeamsToTournament = async () => {
    if (!selectedTournamentId || selectedTeamIdsToAdd.length === 0) return;
    setIsAddingTeams(true);
    setError(null);

    try {
      await Promise.all(selectedTeamIdsToAdd.map((teamId) => tournamentService.addTeam(selectedTournamentId, teamId)));
      setSuccess(`Successfully added ${selectedTeamIdsToAdd.length} team(s) to the tournament!`);
      await loadTournamentDetails(selectedTournamentId);
      setSelectedTeamIdsToAdd([]);
      setTeamSearchTerm('');
      if (isManageTeamsModalOpen) {
        setManageTeamsTab('current');
      } else {
        setTimeout(() => {
          setIsAddTeamsModalOpen(false);
          setSuccess(null);
        }, 1500);
      }
    } catch (err) {
      console.error('Error adding teams:', err);
      setError(err.message || 'Failed to add teams to tournament');
    } finally {
      setIsAddingTeams(false);
    }
  };

  const handleRemoveTeamsFromTournament = async () => {
    if (!selectedTournamentId || teamsToRemove.length === 0) return;
    setIsRemovingTeams(true);
    setError(null);

    try {
      await Promise.all(teamsToRemove.map((teamId) => tournamentService.removeTeam(selectedTournamentId, teamId)));
      setSuccess(`Successfully removed ${teamsToRemove.length} team(s) from the tournament!`);
      await loadTournamentDetails(selectedTournamentId);
      setTeamsToRemove([]);
      setTimeout(() => {
        setIsManageTeamsModalOpen(false);
        setSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error removing teams:', err);
      setError(err.message || 'Failed to remove teams from tournament');
    } finally {
      setIsRemovingTeams(false);
    }
  };

  const handleAssignGroup = async (tournamentTeamId, groupId) => {
    setError(null);
    try {
      await tournamentService.assignTeamToGroup(tournamentTeamId, groupId || null);
      await loadTournamentDetails(selectedTournamentId);
    } catch (err) {
      console.error('Error assigning team to group:', err);
      setError(err.message || 'Failed to assign team to group');
    }
  };

  // ---------------------------------------------------------------------
  // Groups
  // ---------------------------------------------------------------------

  const handleCreateGroup = async () => {
    if (!selectedTournamentId || !newGroupName.trim()) return;
    setIsCreatingGroup(true);
    setError(null);

    try {
      await tournamentService.createGroup(selectedTournamentId, newGroupName.trim(), tournamentDetails?.groups?.length || 0);
      setNewGroupName('');
      await loadTournamentDetails(selectedTournamentId);
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err.message || 'Failed to create group');
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleOpenGroupGenerator = () => {
    const activeTeamCount = (tournamentDetails?.teams || []).filter((tt) => tt.isActive).length;
    const suggestedCount = activeTeamCount >= 4 ? Math.min(4, Math.max(2, Math.floor(activeTeamCount / 3))) : 2;
    setGroupGeneratorSettings({
      groupCount: suggestedCount,
      naming: 'letters',
      assignTeams: true,
      distribution: 'balanced',
      replaceExisting: (tournamentDetails?.groups || []).length > 0,
    });
    setError(null);
    setIsGroupGeneratorOpen(true);
  };

  const handleGroupGeneratorChange = (field, value) => {
    setGroupGeneratorSettings((prev) => ({ ...prev, [field]: value }));
  };

  const getGroupPreviewNames = () => {
    const count = Math.min(16, Math.max(2, Number(groupGeneratorSettings.groupCount) || 2));
    return Array.from({ length: count }, (_, index) =>
      groupGeneratorSettings.naming === 'numbers'
        ? `Group ${index + 1}`
        : `Group ${String.fromCharCode(65 + index)}`
    );
  };

  const handleGenerateGroups = async () => {
    if (!selectedTournamentId) return;
    setIsGeneratingGroups(true);
    setError(null);

    try {
      const result = await tournamentService.generateGroups(selectedTournamentId, {
        groupCount: Number(groupGeneratorSettings.groupCount),
        naming: groupGeneratorSettings.naming,
        assignTeams: groupGeneratorSettings.assignTeams,
        distribution: groupGeneratorSettings.distribution,
        replaceExisting: groupGeneratorSettings.replaceExisting,
      });

      await loadTournamentDetails(selectedTournamentId);
      setIsGroupGeneratorOpen(false);
      setSuccess(
        `Generated ${result.groups.length} group(s)${
          result.assignedCount > 0 ? ` and assigned ${result.assignedCount} team(s)` : ''
        }.`
      );
      clearMessagesLater();
    } catch (err) {
      console.error('Error generating groups:', err);
      setError(err.message || 'Failed to generate groups');
    } finally {
      setIsGeneratingGroups(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    setError(null);
    try {
      await tournamentService.deleteGroup(groupId);
      await loadTournamentDetails(selectedTournamentId);
      setSuccess('Group removed successfully!');
      clearMessagesLater();
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(err.message || 'Failed to delete group');
    }
  };

  // ---------------------------------------------------------------------
  // Fixture generation (round-robin circle method, ported from league-organizer)
  // ---------------------------------------------------------------------

  const getEligibleTeamsForFixtures = (groupId) => {
    return (tournamentDetails?.teams || [])
      .filter((tt) => tt.isActive && tt.team && (!groupId || tt.groupId === groupId))
      .map((tt) => ({ id: tt.team.id, name: tt.team.teamName, logoUrl: tt.team.logoUrl }));
  };

  const handleOpenFixtureGenerator = (groupId = '') => {
    setFixtureSettings({ ...EMPTY_FIXTURE_SETTINGS, groupId: groupId || '' });
    setFixtureStep(1);
    setGeneratedFixtures([]);
    setError(null);
    setIsFixtureModalOpen(true);
  };

  const handleFixtureSettingChange = (field, value) => {
    setFixtureSettings((prev) => ({ ...prev, [field]: value }));
  };

  const generateFixtures = async () => {
    const eligibleTeams = getEligibleTeamsForFixtures(fixtureSettings.groupId);

    if (eligibleTeams.length < 2) {
      setError('At least 2 active teams are required to generate fixtures');
      return;
    }

    setIsGeneratingFixtures(true);
    setError(null);

    try {
      const fixtures = [];
      const teams = [...eligibleTeams];
      let matchId = 1;
      const startDate = new Date(fixtureSettings.startDate);

      const rounds = teams.length - 1;
      const matchesPerRound = Math.floor(teams.length / 2);

      for (let round = 0; round < rounds; round++) {
        for (let match = 0; match < matchesPerRound; match++) {
          const homeIndex = match;
          const awayIndex = teams.length - 1 - match;

          if (homeIndex < awayIndex) {
            const homeTeam = teams[homeIndex];
            const awayTeam = teams[awayIndex];

            const matchDay = Math.floor((round * matchesPerRound + match) / matchesPerRound) * 7;
            const matchDate = new Date(startDate);
            matchDate.setDate(matchDate.getDate() + matchDay);

            const timeSlot = fixtureSettings.timeSlots[match % fixtureSettings.timeSlots.length] || '14:00';
            const venue = fixtureSettings.venues.length > 0 ? fixtureSettings.venues[match % fixtureSettings.venues.length] : 'TBD';

            fixtures.push({
              id: matchId++,
              round: round + 1,
              homeTeamId: homeTeam.id,
              homeTeamName: homeTeam.name,
              awayTeamId: awayTeam.id,
              awayTeamName: awayTeam.name,
              date: matchDate.toISOString().split('T')[0],
              time: timeSlot,
              venue,
              scheduledDate: `${matchDate.toISOString().split('T')[0]}T${timeSlot}:00`,
            });
          }
        }

        const lastTeam = teams.pop();
        teams.splice(1, 0, lastTeam);
      }

      if (fixtureSettings.doubleRoundRobin) {
        const secondLeg = fixtures.map((fixture) => {
          const originalDate = new Date(fixture.scheduledDate);
          const newDate = new Date(originalDate);
          newDate.setDate(newDate.getDate() + rounds * 7);

          return {
            ...fixture,
            id: matchId++,
            round: fixture.round + rounds,
            homeTeamId: fixture.awayTeamId,
            homeTeamName: fixture.awayTeamName,
            awayTeamId: fixture.homeTeamId,
            awayTeamName: fixture.homeTeamName,
            date: newDate.toISOString().split('T')[0],
            scheduledDate: `${newDate.toISOString().split('T')[0]}T${fixture.time}:00`,
          };
        });
        fixtures.push(...secondLeg);
      }

      setGeneratedFixtures(fixtures);
      setFixtureStep(2);
    } catch (err) {
      console.error('Error generating fixtures:', err);
      setError(err.message || 'Failed to generate fixtures');
    } finally {
      setIsGeneratingFixtures(false);
    }
  };

  const handleSaveFixtures = async () => {
    if (!selectedTournamentId || generatedFixtures.length === 0) return;
    setIsGeneratingFixtures(true);
    setError(null);

    try {
      await tournamentService.createGamesBatch(
        generatedFixtures.map((fixture) => ({
          tournamentId: selectedTournamentId,
          groupId: fixtureSettings.groupId || null,
          homeTeamId: fixture.homeTeamId,
          awayTeamId: fixture.awayTeamId,
          scheduledDate: fixture.scheduledDate,
          venue: fixture.venue,
          gameStatus: 'scheduled',
          stageName: GROUP_STAGE_NAME,
          matchNotes: fixtureSettings.groupId
            ? `Group Stage - ${(tournamentDetails?.groups || []).find((g) => g.id === fixtureSettings.groupId)?.name || ''}`
            : GROUP_STAGE_NAME,
        }))
      );

      setSuccess(`Successfully created ${generatedFixtures.length} fixtures!`);
      setFixtureStep(3);
      await loadGames(selectedTournamentId);

      setTimeout(() => {
        setIsFixtureModalOpen(false);
        setFixtureStep(1);
        setGeneratedFixtures([]);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('Error saving fixtures:', err);
      setError(err.message || 'Failed to save fixtures');
    } finally {
      setIsGeneratingFixtures(false);
    }
  };

  // ---------------------------------------------------------------------
  // Fixture list actions: reorder / delete / export
  // ---------------------------------------------------------------------

  const handleMoveFixture = async (fixtureId, direction) => {
    const list = displayGames;
    const currentIndex = list.findIndex((f) => f.id === fixtureId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= list.length) return;

    setIsRearranging(true);
    setError(null);

    try {
      const fixture1 = list[currentIndex];
      const fixture2 = list[newIndex];

      if (fixture1.scheduledDate && fixture2.scheduledDate) {
        const tempDate = fixture1.scheduledDate;
        await Promise.all([
          tournamentService.updateGame(fixture1.id, { scheduledDate: fixture2.scheduledDate, venue: fixture1.venue }),
          tournamentService.updateGame(fixture2.id, { scheduledDate: tempDate, venue: fixture2.venue }),
        ]);
      } else if (fixture1.scheduledDate && !fixture2.scheduledDate) {
        await tournamentService.updateGame(fixture2.id, { scheduledDate: fixture1.scheduledDate, venue: fixture2.venue });
        await tournamentService.updateGame(fixture1.id, { scheduledDate: null, venue: fixture1.venue });
      } else if (!fixture1.scheduledDate && fixture2.scheduledDate) {
        await tournamentService.updateGame(fixture1.id, { scheduledDate: fixture2.scheduledDate, venue: fixture1.venue });
        await tournamentService.updateGame(fixture2.id, { scheduledDate: null, venue: fixture2.venue });
      }

      await loadGames(selectedTournamentId);
      setSuccess('Fixture order updated!');
      clearMessagesLater();
    } catch (err) {
      console.error('Error rearranging fixtures:', err);
      setError(err.message || 'Failed to rearrange fixtures');
    } finally {
      setIsRearranging(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    setError(null);
    try {
      await tournamentService.deleteGame(gameId);
      setGames((prev) => prev.filter((g) => g.id !== gameId));
      setSuccess('Fixture removed successfully!');
      clearMessagesLater();
    } catch (err) {
      console.error('Error deleting fixture:', err);
      setError(err.message || 'Failed to delete fixture');
    }
  };

  const mapGamesForExport = (list) =>
    list.map((game) => ({
      ...game,
      homeTeam: game.homeTeam ? { name: game.homeTeam.teamName, logoUrl: game.homeTeam.logoUrl } : null,
      awayTeam: game.awayTeam ? { name: game.awayTeam.teamName, logoUrl: game.awayTeam.logoUrl } : null,
      matchStatus: game.gameStatus,
    }));

  const handleExportFixtures = async (format) => {
    const fixturesToExport = mapGamesForExport(displayGames);
    const tournamentName = tournamentDetails?.name || 'Tournament';
    const filename = `${tournamentName.toLowerCase().replace(/\s+/g, '-')}-fixtures`;

    try {
      switch (format) {
        case 'pdf':
          await exportFixturesToPDF(fixturesToExport, tournamentName, filename);
          break;
        case 'excel':
          await exportFixturesToExcel(fixturesToExport, tournamentName, filename);
          break;
        case 'word':
          exportFixturesToWord(fixturesToExport, tournamentName, filename);
          break;
        default:
          break;
      }
      setSuccess(`Fixtures exported to ${format.toUpperCase()} successfully!`);
      clearMessagesLater();
    } catch (err) {
      console.error(`Error exporting to ${format}:`, err);
      setError(`Failed to export fixtures to ${format.toUpperCase()}`);
    }
  };

  // ---------------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------------

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(dateString));
    } catch {
      return '-';
    }
  };

  const getGameStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-primary/10 text-primary';
      case 'live':
        return 'bg-error/10 text-error';
      case 'completed':
        return 'bg-success/10 text-success';
      case 'postponed':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTournamentStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'upcoming':
        return 'bg-primary/10 text-primary';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const groupOptions = [{ value: '', label: 'No Group' }, ...(tournamentDetails?.groups || []).map((g) => ({ value: g.id, label: g.name }))];

  const fixtureGroupOptions = [
    { value: '', label: 'Whole Tournament (all active teams)' },
    ...(tournamentDetails?.groups || []).map((g) => ({ value: g.id, label: `Group: ${g.name}` })),
  ];

  const activeTeamsForKnockout = (tournamentDetails?.teams || [])
    .filter((tt) => tt.isActive && tt.team)
    .map((tt) => ({ id: tt.team.id, name: tt.team.teamName, logoUrl: tt.team.logoUrl }));

  const getGroupName = (groupId) =>
    (tournamentDetails?.groups || []).find((g) => g.id === groupId)?.name || null;

  const openScheduleGameModal = (scope = 'group', groupId = '') => {
    setScheduleGameInitialScope(scope);
    setScheduleGameInitialGroupId(groupId || '');
    setIsScheduleGameModalOpen(true);
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="ShieldAlert" size={48} className="text-warning mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Tournament Management' },
    { label: 'Games Organizer' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="CalendarDays" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Tournament Games Organizer</h1>
                  <p className="text-muted-foreground">Manage tournaments, teams, groups and fixtures</p>
                </div>
              </div>
              <Button variant="default" onClick={handleOpenCreateTournamentModal} iconName="Plus" iconPosition="left">
                Create Tournament
              </Button>
            </div>
          </div>

          {success && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={18} />
                <p className="font-medium">{success}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} />
                <p className="font-medium">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={48} className="text-primary animate-spin" />
            </div>
          ) : tournaments.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center card-shadow">
              <Icon name="Trophy" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Tournaments Found</h3>
              <p className="text-muted-foreground mb-4">Create your first tournament to get started</p>
              <Button variant="default" onClick={handleOpenCreateTournamentModal} iconName="Plus" iconPosition="left">
                Create Tournament
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tournaments List */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-card border border-border rounded-lg p-4 card-shadow">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Tournaments</h2>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {tournaments.map((tournament) => (
                      <div
                        key={tournament.id}
                        onClick={() => setSelectedTournamentId(tournament.id)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedTournamentId === tournament.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground">{tournament.name}</h3>
                            <p className="text-sm text-muted-foreground">{tournament.season}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-xs px-2 py-1 rounded-full capitalize ${getTournamentStatusColor(tournament.tournamentStatus)}`}>
                                {tournament.tournamentStatus || 'upcoming'}
                              </span>
                            </div>
                          </div>
                          <Icon
                            name={selectedTournamentId === tournament.id ? 'ChevronRight' : 'ChevronLeft'}
                            size={16}
                            className="text-muted-foreground"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tournament Details */}
              <div className="lg:col-span-2 space-y-6">
                {!selectedTournamentId ? (
                  <div className="bg-card border border-border rounded-lg p-12 text-center card-shadow">
                    <Icon name="Trophy" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Select a Tournament</h3>
                    <p className="text-muted-foreground">Choose a tournament from the list to view details and manage fixtures</p>
                  </div>
                ) : isLoadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader2" size={48} className="text-primary animate-spin" />
                  </div>
                ) : tournamentDetails ? (
                  <>
                    {/* Tournament Header */}
                    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                        <div>
                          <h2 className="text-2xl font-bold text-foreground">{tournamentDetails.name}</h2>
                          <p className="text-muted-foreground">{tournamentDetails.season}</p>
                          {tournamentDetails.description && (
                            <p className="text-sm text-muted-foreground mt-2">{tournamentDetails.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button variant="outline" size="sm" onClick={handleOpenEditTournamentModal} iconName="Edit">
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsKnockoutModalOpen(true)}
                            iconName="GitBranch"
                            disabled={activeTeamsForKnockout.length < 2}
                          >
                            Knockout Bracket
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openScheduleGameModal('group')}
                            iconName="CalendarPlus"
                            disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                          >
                            Schedule Game
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleOpenFixtureGenerator('')}
                            iconName="Calendar"
                            disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                          >
                            Generate Fixtures
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => setIsDeleteModalOpen(true)} iconName="Trash2">
                            Delete
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Status</p>
                          <p className="font-semibold text-foreground capitalize">{tournamentDetails.tournamentStatus || 'upcoming'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Teams</p>
                          <p className="font-semibold text-foreground">
                            {(tournamentDetails.teams || []).length} / {tournamentDetails.maxTeams || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Start Date</p>
                          <p className="font-semibold text-foreground">{formatDate(tournamentDetails.startDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">End Date</p>
                          <p className="font-semibold text-foreground">{formatDate(tournamentDetails.endDate)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Teams */}
                    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-foreground">Teams in Tournament</h3>
                        <div className="flex items-center gap-2">
                          <Button variant="default" size="sm" onClick={handleOpenAddTeamsModal} iconName="Plus" iconPosition="left">
                            Add Teams
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleOpenManageTeamsModal} iconName="Users">
                            Manage Teams
                          </Button>
                        </div>
                      </div>

                      {!tournamentDetails.teams || tournamentDetails.teams.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">No teams added to this tournament yet</p>
                          <Button variant="default" onClick={handleOpenAddTeamsModal} iconName="Plus" iconPosition="left">
                            Add Teams
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {tournamentDetails.teams.map((tt) => (
                            <div key={tt.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                              {tt.team?.logoUrl ? (
                                <Image src={tt.team.logoUrl} alt={tt.team.teamName} className="w-12 h-12 rounded-lg object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                                  <Icon name="Shield" size={24} className="text-muted-foreground" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">{tt.team?.teamName || 'Unknown Team'}</p>
                                <p className="text-xs text-muted-foreground">{tt.isActive ? 'Active' : 'Inactive'}</p>
                                <Select
                                  className="mt-2"
                                  options={groupOptions}
                                  value={tt.groupId || ''}
                                  onChange={(value) => handleAssignGroup(tt.id, value)}
                                  placeholder="Assign group"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Groups */}
                    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h3 className="text-lg font-semibold text-foreground">Groups</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleOpenGroupGenerator}
                          iconName="Sparkles"
                          iconPosition="left"
                        >
                          Generate Groups
                        </Button>
                      </div>

                      <div className="flex items-center gap-2 mb-4">
                        <Input
                          placeholder="New group name (e.g. Group A)"
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          onClick={handleCreateGroup}
                          loading={isCreatingGroup}
                          disabled={!newGroupName.trim()}
                          iconName="Plus"
                          iconPosition="left"
                        >
                          Add Group
                        </Button>
                      </div>

                      {!tournamentDetails.groups || tournamentDetails.groups.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No groups created yet. Use Generate Groups to auto-create pools and assign teams, or add groups manually.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {tournamentDetails.groups.map((group) => {
                            const teamCount = (tournamentDetails.teams || []).filter((tt) => tt.groupId === group.id).length;
                            return (
                              <div
                                key={group.id}
                                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                              >
                                <div>
                                  <p className="font-medium text-foreground">{group.name}</p>
                                  <p className="text-xs text-muted-foreground">{teamCount} team(s)</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openScheduleGameModal('group', group.id)}
                                    iconName="CalendarPlus"
                                    disabled={teamCount < 2}
                                  >
                                    Schedule Game
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenFixtureGenerator(group.id)}
                                    iconName="Calendar"
                                    disabled={teamCount < 2}
                                  >
                                    Generate Fixtures
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteGroup(group.id)}
                                    iconName="Trash2"
                                    className="text-destructive"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Fixtures */}
                    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-foreground">Tournament Fixtures</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-muted-foreground">
                            {games.length} match{games.length !== 1 ? 'es' : ''}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openScheduleGameModal('group')}
                            iconName="CalendarPlus"
                            iconPosition="left"
                            disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                          >
                            Schedule Game
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openScheduleGameModal('knockout')}
                            iconName="GitBranch"
                            iconPosition="left"
                            disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                          >
                            Schedule Knockout
                          </Button>
                        </div>
                      </div>

                      {games.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-muted/30 rounded-lg border border-border">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Sort by:</span>
                            <div className="flex items-center gap-1">
                              {[
                                { value: 'date', label: 'Date' },
                                { value: 'time', label: 'Time' },
                                { value: 'team', label: 'Team' },
                                { value: 'venue', label: 'Venue' },
                              ].map((option) => (
                                <Button
                                  key={option.value}
                                  variant={gameSortBy === option.value ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => handleSortChange(option.value)}
                                  className="h-7 text-xs"
                                >
                                  {option.label}
                                  {gameSortBy === option.value && (
                                    <Icon name={gameSortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={12} className="ml-1" />
                                  )}
                                </Button>
                              ))}
                            </div>
                          </div>

                          <div className="h-4 w-px bg-border" />

                          <div className="flex items-center gap-1">
                            <span className="text-sm text-muted-foreground">Export:</span>
                            <Button variant="ghost" size="sm" onClick={() => handleExportFixtures('pdf')} className="h-7 px-2" title="Export to PDF">
                              <Icon name="FileText" size={14} className="mr-1" />
                              <span className="text-xs">PDF</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportFixtures('excel')}
                              className="h-7 px-2"
                              title="Export to Excel"
                            >
                              <Icon name="Table" size={14} className="mr-1" />
                              <span className="text-xs">Excel</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportFixtures('word')}
                              className="h-7 px-2"
                              title="Export to Word"
                            >
                              <Icon name="File" size={14} className="mr-1" />
                              <span className="text-xs">Word</span>
                            </Button>
                          </div>
                        </div>
                      )}

                      {isLoadingGames ? (
                        <div className="flex items-center justify-center py-8">
                          <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                        </div>
                      ) : games.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">No fixtures scheduled for this tournament yet</p>
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <Button
                              variant="default"
                              onClick={() => openScheduleGameModal('group')}
                              iconName="CalendarPlus"
                              iconPosition="left"
                              disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                            >
                              Schedule Game
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleOpenFixtureGenerator('')}
                              iconName="Calendar"
                              iconPosition="left"
                              disabled={(tournamentDetails.teams || []).filter((t) => t.isActive).length < 2}
                            >
                              Generate Fixtures
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {displayGames.map((game, index) => (
                            <div
                              key={game.id}
                              className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors"
                            >
                              <div className="flex flex-col gap-1 mr-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveFixture(game.id, 'up')}
                                  disabled={index === 0 || isRearranging}
                                  className="h-6 w-6 p-0"
                                  title="Move up"
                                >
                                  <Icon name="ChevronUp" size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveFixture(game.id, 'down')}
                                  disabled={index === displayGames.length - 1 || isRearranging}
                                  className="h-6 w-6 p-0"
                                  title="Move down"
                                >
                                  <Icon name="ChevronDown" size={14} />
                                </Button>
                              </div>

                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className="flex-shrink-0 text-center min-w-[100px]">
                                  <p className="text-xs text-muted-foreground">Date</p>
                                  <p className="text-sm font-medium text-foreground">{formatDate(game.scheduledDate)}</p>
                                  {game.scheduledDate && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {new Date(game.scheduledDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {game.homeTeam?.logoUrl ? (
                                      <Image src={game.homeTeam.logoUrl} alt={game.homeTeam.teamName} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                    ) : (
                                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                        <Icon name="Shield" size={16} className="text-muted-foreground" />
                                      </div>
                                    )}
                                    <p className="text-sm font-medium text-foreground truncate">{game.homeTeam?.teamName || 'TBD'}</p>
                                  </div>

                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {game.gameStatus === 'completed' && (
                                      <>
                                        <span className="text-sm font-bold text-foreground">{game.homeScore || 0}</span>
                                        <span className="text-muted-foreground">-</span>
                                        <span className="text-sm font-bold text-foreground">{game.awayScore || 0}</span>
                                      </>
                                    )}
                                    {game.gameStatus === 'live' && (
                                      <>
                                        <span className="text-sm font-bold text-error">{game.homeScore || 0}</span>
                                        <span className="text-error animate-pulse">•</span>
                                        <span className="text-sm font-bold text-error">{game.awayScore || 0}</span>
                                      </>
                                    )}
                                    {(!game.gameStatus || game.gameStatus === 'scheduled') && (
                                      <span className="text-xs text-muted-foreground">vs</span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                                    <p className="text-sm font-medium text-foreground truncate text-right">{game.awayTeam?.teamName || 'TBD'}</p>
                                    {game.awayTeam?.logoUrl ? (
                                      <Image src={game.awayTeam.logoUrl} alt={game.awayTeam.teamName} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                    ) : (
                                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                        <Icon name="Shield" size={16} className="text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {game.groupId && getGroupName(game.groupId) && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                                      {getGroupName(game.groupId)}
                                    </span>
                                  )}
                                  {game.stageName && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-secondary/10 text-secondary-foreground">{game.stageName}</span>
                                  )}
                                  {game.venue && (
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground">Venue</p>
                                      <p className="text-xs font-medium text-foreground truncate max-w-[120px]">{game.venue}</p>
                                    </div>
                                  )}
                                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${getGameStatusColor(game.gameStatus)}`}>
                                    {game.gameStatus || 'scheduled'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteGame(game.id)}
                                    className="h-7 w-7 p-0 text-destructive"
                                    title="Remove fixture"
                                  >
                                    <Icon name="Trash2" size={14} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-12 text-center card-shadow">
                    <Icon name="AlertTriangle" size={48} className="text-warning mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Tournament Not Found</h3>
                    <p className="text-muted-foreground">The selected tournament could not be loaded</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create/Edit Tournament Modal */}
          {isTournamentModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsTournamentModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Trophy" size={20} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">
                      {tournamentModalMode === 'create' ? 'Create Tournament' : 'Edit Tournament'}
                    </h2>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setIsTournamentModalOpen(false)} iconName="X" />
                </div>

                <form onSubmit={handleSaveTournament} className="p-6 space-y-4">
                  <Input
                    label="Tournament Name"
                    required
                    value={tournamentForm.name}
                    onChange={(e) => handleTournamentFormChange('name', e.target.value)}
                    placeholder="e.g. National Cup 2026"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Season"
                      value={tournamentForm.season}
                      onChange={(e) => handleTournamentFormChange('season', e.target.value)}
                      placeholder="e.g. 2026"
                    />
                    <Select
                      label="Status"
                      options={STATUS_OPTIONS}
                      value={tournamentForm.tournamentStatus}
                      onChange={(value) => handleTournamentFormChange('tournamentStatus', value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                    <textarea
                      value={tournamentForm.description}
                      onChange={(e) => handleTournamentFormChange('description', e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Start Date"
                      type="date"
                      value={tournamentForm.startDate}
                      onChange={(e) => handleTournamentFormChange('startDate', e.target.value)}
                    />
                    <Input
                      label="End Date"
                      type="date"
                      value={tournamentForm.endDate}
                      onChange={(e) => handleTournamentFormChange('endDate', e.target.value)}
                    />
                    <Input
                      label="Max Teams"
                      type="number"
                      min="2"
                      value={tournamentForm.maxTeams}
                      onChange={(e) => handleTournamentFormChange('maxTeams', e.target.value)}
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                    <Button type="button" variant="outline" onClick={() => setIsTournamentModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="default" loading={isSavingTournament} iconName="Save" iconPosition="left">
                      {tournamentModalMode === 'create' ? 'Create Tournament' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Teams Modal */}
          {isAddTeamsModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsAddTeamsModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Add Teams to Tournament</h2>
                      <p className="text-sm text-muted-foreground">{tournamentDetails?.name}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAddTeamsModalOpen(false);
                      setSelectedTeamIdsToAdd([]);
                      setTeamSearchTerm('');
                    }}
                    iconName="X"
                  />
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <Input
                      label="Search Teams"
                      placeholder="Search by team name..."
                      value={teamSearchTerm}
                      onChange={(e) => setTeamSearchTerm(e.target.value)}
                      iconName="Search"
                    />
                  </div>

                  {filteredAvailableTeams.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {teamSearchTerm ? 'No teams found matching your search' : 'No available regional teams to add'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground">
                          {selectedTeamIdsToAdd.length} team(s) selected • {filteredAvailableTeams.length} available
                        </p>
                      </div>
                      <div className="max-h-96 overflow-y-auto space-y-2 border border-border rounded-lg p-4">
                        {filteredAvailableTeams.map((team) => {
                          const isSelected = selectedTeamIdsToAdd.includes(team.id);
                          return (
                            <div
                              key={team.id}
                              onClick={() => handleToggleTeamSelection(team.id)}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected ? 'border-primary bg-primary' : 'border-border'
                                }`}
                              >
                                {isSelected && <Icon name="Check" size={14} className="text-primary-foreground" />}
                              </div>
                              {team.logoUrl ? (
                                <Image src={team.logoUrl} alt={team.teamName} className="w-10 h-10 rounded-lg object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                                  <Icon name="Shield" size={20} className="text-muted-foreground" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">{team.teamName}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddTeamsModalOpen(false);
                        setSelectedTeamIdsToAdd([]);
                        setTeamSearchTerm('');
                      }}
                      disabled={isAddingTeams}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleAddTeamsToTournament}
                      loading={isAddingTeams}
                      disabled={selectedTeamIdsToAdd.length === 0 || isAddingTeams}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add {selectedTeamIdsToAdd.length > 0 ? `${selectedTeamIdsToAdd.length} ` : ''}Team
                      {selectedTeamIdsToAdd.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Manage Teams Modal */}
          {isManageTeamsModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsManageTeamsModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Manage Tournament Teams</h2>
                      <p className="text-sm text-muted-foreground">{tournamentDetails?.name}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsManageTeamsModalOpen(false);
                      setTeamsToRemove([]);
                      setSelectedTeamIdsToAdd([]);
                      setTeamSearchTerm('');
                    }}
                    iconName="X"
                  />
                </div>

                <div className="px-6 pt-4 border-b border-border">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setManageTeamsTab('current')}
                      className={`pb-3 px-1 border-b-2 transition-colors ${
                        manageTeamsTab === 'current' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Current Teams ({tournamentDetails?.teams?.length || 0})
                    </button>
                    <button
                      onClick={() => setManageTeamsTab('add')}
                      className={`pb-3 px-1 border-b-2 transition-colors ${
                        manageTeamsTab === 'add' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Add Teams
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {manageTeamsTab === 'current' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-foreground">Teams in Tournament</h3>
                        {teamsToRemove.length > 0 && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleRemoveTeamsFromTournament}
                            loading={isRemovingTeams}
                            iconName="Trash2"
                            iconPosition="left"
                          >
                            Remove {teamsToRemove.length} Team{teamsToRemove.length !== 1 ? 's' : ''}
                          </Button>
                        )}
                      </div>

                      {!tournamentDetails?.teams || tournamentDetails.teams.length === 0 ? (
                        <div className="text-center py-12">
                          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">No teams in this tournament</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {tournamentDetails.teams.map((tt) => {
                            const isSelected = teamsToRemove.includes(tt.regionalTeamId);
                            return (
                              <div
                                key={tt.id}
                                onClick={() => handleToggleTeamRemoval(tt.regionalTeamId)}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected ? 'border-error bg-error/5' : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected ? 'border-error bg-error' : 'border-border'
                                  }`}
                                >
                                  {isSelected && <Icon name="Check" size={14} className="text-error-foreground" />}
                                </div>
                                {tt.team?.logoUrl ? (
                                  <Image src={tt.team.logoUrl} alt={tt.team.teamName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                    <Icon name="Shield" size={20} className="text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-foreground truncate">{tt.team?.teamName || 'Unknown Team'}</p>
                                </div>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    tt.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                                  }`}
                                >
                                  {tt.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {manageTeamsTab === 'add' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-foreground">Available Teams</h3>
                        {selectedTeamIdsToAdd.length > 0 && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleAddTeamsToTournament}
                            loading={isAddingTeams}
                            iconName="Plus"
                            iconPosition="left"
                          >
                            Add {selectedTeamIdsToAdd.length} Team{selectedTeamIdsToAdd.length !== 1 ? 's' : ''}
                          </Button>
                        )}
                      </div>

                      <div className="mb-4">
                        <Input
                          label="Search Teams"
                          placeholder="Search by team name..."
                          value={teamSearchTerm}
                          onChange={(e) => setTeamSearchTerm(e.target.value)}
                          iconName="Search"
                        />
                      </div>

                      {filteredAvailableTeams.length === 0 ? (
                        <div className="text-center py-12">
                          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">
                            {teamSearchTerm ? 'No teams found matching your search' : 'No available teams to add'}
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto space-y-2 border border-border rounded-lg p-4">
                          {filteredAvailableTeams.map((team) => {
                            const isSelected = selectedTeamIdsToAdd.includes(team.id);
                            return (
                              <div
                                key={team.id}
                                onClick={() => handleToggleTeamSelection(team.id)}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected ? 'border-primary bg-primary' : 'border-border'
                                  }`}
                                >
                                  {isSelected && <Icon name="Check" size={14} className="text-primary-foreground" />}
                                </div>
                                {team.logoUrl ? (
                                  <Image src={team.logoUrl} alt={team.teamName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                    <Icon name="Shield" size={20} className="text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-foreground truncate">{team.teamName}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsManageTeamsModalOpen(false);
                        setTeamsToRemove([]);
                        setSelectedTeamIdsToAdd([]);
                        setTeamSearchTerm('');
                      }}
                      disabled={isRemovingTeams || isAddingTeams}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Group Generator Modal */}
          {isGroupGeneratorOpen && tournamentDetails && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => !isGeneratingGroups && setIsGroupGeneratorOpen(false)}
              />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Layers" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Generate Groups</h2>
                      <p className="text-sm text-muted-foreground">{tournamentDetails.name}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsGroupGeneratorOpen(false)}
                    iconName="X"
                    disabled={isGeneratingGroups}
                  />
                </div>

                <div className="p-6 space-y-5">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      Active teams available:{' '}
                      <span className="font-semibold text-foreground">
                        {(tournamentDetails.teams || []).filter((tt) => tt.isActive).length}
                      </span>
                    </p>
                    {(tournamentDetails.groups || []).length > 0 && (
                      <p className="text-xs text-warning mt-2">
                        This tournament already has {(tournamentDetails.groups || []).length} group(s).
                        Enable replace to regenerate.
                      </p>
                    )}
                  </div>

                  <Input
                    label="Number of Groups"
                    type="number"
                    min={2}
                    max={16}
                    value={groupGeneratorSettings.groupCount}
                    onChange={(e) => handleGroupGeneratorChange('groupCount', e.target.value)}
                    description="Between 2 and 16 groups"
                  />

                  <Select
                    label="Group Naming"
                    options={[
                      { value: 'letters', label: 'Group A, B, C…' },
                      { value: 'numbers', label: 'Group 1, 2, 3…' },
                    ]}
                    value={groupGeneratorSettings.naming}
                    onChange={(value) => handleGroupGeneratorChange('naming', value)}
                  />

                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={groupGeneratorSettings.assignTeams}
                        onChange={(e) => handleGroupGeneratorChange('assignTeams', e.target.checked)}
                      />
                      <span>
                        <span className="block text-sm font-medium text-foreground">Auto-assign teams</span>
                        <span className="block text-xs text-muted-foreground">
                          Distribute active teams across the generated groups
                        </span>
                      </span>
                    </label>

                    {groupGeneratorSettings.assignTeams && (
                      <Select
                        label="Distribution Method"
                        options={[
                          { value: 'balanced', label: 'Balanced (snake draft)' },
                          { value: 'random', label: 'Random shuffle' },
                        ]}
                        value={groupGeneratorSettings.distribution}
                        onChange={(value) => handleGroupGeneratorChange('distribution', value)}
                      />
                    )}

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={groupGeneratorSettings.replaceExisting}
                        onChange={(e) => handleGroupGeneratorChange('replaceExisting', e.target.checked)}
                      />
                      <span>
                        <span className="block text-sm font-medium text-foreground">Replace existing groups</span>
                        <span className="block text-xs text-muted-foreground">
                          Deletes current groups before generating new ones
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="rounded-lg border border-border p-4">
                    <p className="text-sm font-medium text-foreground mb-2">Preview</p>
                    <div className="flex flex-wrap gap-2">
                      {getGroupPreviewNames().map((name) => (
                        <span
                          key={name}
                          className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => setIsGroupGeneratorOpen(false)}
                    disabled={isGeneratingGroups}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGenerateGroups}
                    loading={isGeneratingGroups}
                    iconName="Sparkles"
                    iconPosition="left"
                    disabled={
                      Number(groupGeneratorSettings.groupCount) < 2 ||
                      Number(groupGeneratorSettings.groupCount) > 16
                    }
                  >
                    Generate Groups
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Fixture Generator Modal */}
          {isFixtureModalOpen && tournamentDetails && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsFixtureModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Calendar" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Generate Fixtures</h2>
                      <p className="text-sm text-muted-foreground">{tournamentDetails.name}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsFixtureModalOpen(false);
                      setFixtureStep(1);
                      setGeneratedFixtures([]);
                    }}
                    iconName="X"
                  />
                </div>

                <div className="px-6 py-4 border-b border-border">
                  <div className="flex items-center space-x-4">
                    {[1, 2, 3].map((stepNum) => (
                      <div key={stepNum} className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            fixtureStep >= stepNum ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {stepNum}
                        </div>
                        <span className={`ml-2 text-sm ${fixtureStep >= stepNum ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {stepNum === 1 ? 'Settings' : stepNum === 2 ? 'Preview' : 'Confirm'}
                        </span>
                        {stepNum < 3 && <Icon name="ChevronRight" size={16} className="mx-4 text-muted-foreground" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6">
                  {fixtureStep === 1 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-medium text-foreground">Fixture Generation Settings</h3>

                      <Select
                        label="Scope"
                        description="Generate for the whole tournament or a single group"
                        options={fixtureGroupOptions}
                        value={fixtureSettings.groupId}
                        onChange={(value) => handleFixtureSettingChange('groupId', value)}
                      />

                      <div className="bg-muted/30 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-2">Eligible Teams:</p>
                        <p className="font-semibold text-foreground">
                          {getEligibleTeamsForFixtures(fixtureSettings.groupId).length} active team(s)
                        </p>
                        {getEligibleTeamsForFixtures(fixtureSettings.groupId).length < 2 && (
                          <p className="text-xs text-warning mt-2">At least 2 active teams are required to generate fixtures</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Start Date"
                          type="date"
                          value={fixtureSettings.startDate}
                          onChange={(e) => handleFixtureSettingChange('startDate', e.target.value)}
                          required
                        />
                        <Input
                          label="End Date (informational)"
                          type="date"
                          value={fixtureSettings.endDate}
                          onChange={(e) => handleFixtureSettingChange('endDate', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Time Slots</label>
                        <div className="flex flex-wrap gap-2">
                          {fixtureSettings.timeSlots.map((time, index) => (
                            <div key={index} className="flex items-center space-x-2 bg-muted rounded-md px-3 py-2">
                              <span className="text-sm text-foreground">{time}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newSlots = fixtureSettings.timeSlots.filter((_, i) => i !== index);
                                  handleFixtureSettingChange('timeSlots', newSlots);
                                }}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const time = prompt('Enter time (HH:MM format):');
                              if (time && /^\d{2}:\d{2}$/.test(time)) {
                                handleFixtureSettingChange('timeSlots', [...fixtureSettings.timeSlots, time]);
                              }
                            }}
                            iconName="Plus"
                          >
                            Add Time
                          </Button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Venues</label>
                        <Input
                          type="text"
                          placeholder="Enter venue names separated by commas"
                          value={fixtureSettings.venues.join(', ')}
                          onChange={(e) => {
                            const venues = e.target.value.split(',').map((v) => v.trim()).filter((v) => v);
                            handleFixtureSettingChange('venues', venues);
                          }}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Separate multiple venues with commas</p>
                      </div>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={fixtureSettings.doubleRoundRobin}
                          onChange={(e) => handleFixtureSettingChange('doubleRoundRobin', e.target.checked)}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">Double Round-Robin (Home & Away)</span>
                      </label>

                      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                        <Button variant="outline" onClick={() => setIsFixtureModalOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          onClick={generateFixtures}
                          loading={isGeneratingFixtures}
                          iconName="Zap"
                          iconPosition="left"
                          disabled={!fixtureSettings.startDate || getEligibleTeamsForFixtures(fixtureSettings.groupId).length < 2}
                        >
                          Generate Fixtures
                        </Button>
                      </div>
                    </div>
                  )}

                  {fixtureStep === 2 && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-foreground">Fixture Preview</h3>
                        <div className="text-sm text-muted-foreground">{generatedFixtures.length} matches generated</div>
                      </div>

                      <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="space-y-4">
                          {generatedFixtures.slice(0, 30).map((fixture, index) => (
                            <div key={index} className="flex items-center justify-between bg-card rounded-md p-3 border border-border">
                              <div className="flex items-center space-x-4">
                                <div className="text-sm font-medium text-foreground">Round {fixture.round}</div>
                                <div className="text-sm text-muted-foreground">
                                  {fixture.homeTeamName} vs {fixture.awayTeamName}
                                </div>
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                <span>{fixture.date}</span>
                                <span>{fixture.time}</span>
                                <span>{fixture.venue}</span>
                              </div>
                            </div>
                          ))}
                          {generatedFixtures.length > 30 && (
                            <div className="text-center text-sm text-muted-foreground">
                              ... and {generatedFixtures.length - 30} more matches
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <Button variant="outline" onClick={() => setFixtureStep(1)} iconName="ChevronLeft" iconPosition="left">
                          Back to Settings
                        </Button>
                        <Button variant="default" onClick={handleSaveFixtures} loading={isGeneratingFixtures} iconName="Check" iconPosition="left">
                          Save Fixtures
                        </Button>
                      </div>
                    </div>
                  )}

                  {fixtureStep === 3 && (
                    <div className="text-center space-y-6 py-8">
                      <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                        <Icon name="CheckCircle" size={32} className="text-success" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground">Fixtures Generated Successfully!</h3>
                        <p className="text-muted-foreground mt-2">
                          {generatedFixtures.length} matches have been created for {tournamentDetails.name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Knockout Bracket Modal */}
          {isKnockoutModalOpen && selectedTournamentId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsKnockoutModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <TournamentKnockoutBracket
                    tournamentId={selectedTournamentId}
                    teams={activeTeamsForKnockout}
                    onClose={() => setIsKnockoutModalOpen(false)}
                    onSuccess={() => {
                      loadGames(selectedTournamentId);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <ScheduleTournamentGameModal
            isOpen={isScheduleGameModalOpen}
            onClose={() => setIsScheduleGameModalOpen(false)}
            onSuccess={async () => {
              setSuccess('Fixture scheduled successfully');
              clearMessagesLater();
              if (selectedTournamentId) await loadGames(selectedTournamentId);
            }}
            tournamentId={selectedTournamentId}
            tournamentName={tournamentDetails?.name}
            groups={tournamentDetails?.groups || []}
            teams={tournamentDetails?.teams || []}
            initialGroupId={scheduleGameInitialGroupId}
            initialScope={scheduleGameInitialScope}
          />

          {/* Delete Tournament Modal */}
          {isDeleteModalOpen && selectedTournamentId && tournamentDetails && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsDeleteModalOpen(false)} />
              <div className="relative bg-card border border-border rounded-lg modal-shadow w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                      <Icon name="AlertTriangle" size={24} className="text-destructive" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">Delete Tournament</h3>
                      <p className="text-sm text-muted-foreground mt-1">This action cannot be undone</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-foreground mb-2">
                      Are you sure you want to delete <span className="font-semibold">{tournamentDetails.name}</span>?
                    </p>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                      <p className="text-muted-foreground">
                        <Icon name="Info" size={16} className="inline mr-2" />
                        This will permanently delete the tournament, its groups, team assignments and fixtures.
                      </p>
                      {tournamentDetails.teams && tournamentDetails.teams.length > 0 && (
                        <p className="text-warning">
                          <Icon name="AlertCircle" size={16} className="inline mr-2" />
                          This tournament has {tournamentDetails.teams.length} team(s) registered.
                        </p>
                      )}
                      {games && games.length > 0 && (
                        <p className="text-warning">
                          <Icon name="AlertCircle" size={16} className="inline mr-2" />
                          This tournament has {games.length} fixture(s) scheduled.
                        </p>
                      )}
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="mb-4 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success text-sm">{success}</div>
                  )}

                  <div className="flex items-center justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsDeleteModalOpen(false);
                        setError(null);
                      }}
                      disabled={isDeletingTournament}
                    >
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleDeleteTournament} loading={isDeletingTournament} iconName="Trash2" iconPosition="left">
                      {isDeletingTournament ? 'Deleting...' : 'Delete Tournament'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TournamentGamesOrganizer;
